<template>
  <div class="inspection-report-container">
    <v-container fluid fill-height>
      <!-- Back Button -->
      <v-btn icon class="back-button" @click="confirmBackNavigation">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>

      <!-- Header -->
      <v-row class="pa-7" justify="center">
        <h1 class="page-title">EDIT JOB INSPECTION REPORT</h1>
      </v-row>

      <!-- Main Content Card -->
      <v-row class="fill-height pa-0 ma-0" justify="center" align="start">
        <v-col cols="12" md="10" class="pa-0">
          <v-card elevation="0" class="main-card">
            <v-card-text class="pa-8">
              <v-overlay :value="loading">
                <v-progress-circular indeterminate size="64" />
              </v-overlay>

              <v-form ref="form" v-model="valid">
                <!-- JOB INFORMATION Section -->
                <div class="section-header mb-6">
                  <h2 class="section-title">JOB INFORMATION</h2>
                  <div class="section-divider"></div>
                </div>

                <!-- Row 1: Job Number, Customer Name, Contact Person, Customer Reference -->
                <v-row>
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model="jobNumber"
                      label="Job Number"
                      dense
                      outlined
                      prepend-inner-icon="mdi-numeric"
                      readonly
                      class="nexus-input"
                    />
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model="customerName"
                      label="Customer Name"
                      dense
                      outlined
                      prepend-inner-icon="mdi-account-group"
                      readonly
                      class="nexus-input"
                    />
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model="contactPerson"
                      label="Contact Person"
                      dense
                      outlined
                      prepend-inner-icon="mdi-account"
                      readonly
                      class="nexus-input"
                    />
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model="customerReference"
                      label="Customer Reference"
                      dense
                      outlined
                      prepend-inner-icon="mdi-file-document-outline"
                      :readonly="!isInspectionStatus"
                      class="nexus-input"
                    />
                  </v-col>
                </v-row>

                <!-- Row 2: Seal Type, Serial Number, Shaft Size, Total Lead Time -->
                <v-row>
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model="sealType"
                      label="Seal Type"
                      dense
                      outlined
                      prepend-inner-icon="mdi-cog"
                      :readonly="!isInspectionStatus"
                      class="nexus-input"
                    />
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model="serialNumber"
                      label="Serial Number"
                      dense
                      outlined
                      prepend-inner-icon="mdi-barcode"
                      :readonly="!isInspectionStatus"
                      class="nexus-input"
                    />
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model="shaftSize"
                      label="Shaft Size"
                      dense
                      outlined
                      prepend-inner-icon="mdi-ruler"
                      :readonly="!isInspectionStatus"
                      class="nexus-input"
                    />
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-select
                      v-model="TotalLead"
                      :items="leadTimeOptions"
                      label="Total Lead Time"
                      dense
                      outlined
                      prepend-inner-icon="mdi-clock"
                      :readonly="!isInspectionStatus"
                      class="nexus-input"
                    />
                  </v-col>
                </v-row>

                <!-- Row 3: Engineer, Technician, Salesperson, Sample Number -->
                <v-row>
                  <v-col cols="12" md="3">
                    <v-select
                      v-model="engineer"
                      :items="engineerOptions"
                      item-text="name"
                      item-value="name"
                      label="Engineer"
                      dense
                      outlined
                      prepend-inner-icon="mdi-account-hard-hat"
                      :readonly="!isInspectionStatus"
                      class="nexus-input"
                    />
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-select
                      v-model="technician"
                      :items="technicianOptions"
                      item-text="name"
                      item-value="name"
                      label="Technician"
                      dense
                      outlined
                      prepend-inner-icon="mdi-account-wrench"
                      :readonly="!isInspectionStatus"
                      class="nexus-input"
                    />
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-select
                      v-model="salesperson"
                      :items="salespersonOptions"
                      item-text="name"
                      item-value="name"
                      label="Salesperson"
                      dense
                      outlined
                      prepend-inner-icon="mdi-account-tie"
                      :readonly="!isInspectionStatus"
                      class="nexus-input"
                    />
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model="sampleNumber"
                      label="Sample Number (Optional)"
                      dense
                      outlined
                      prepend-inner-icon="mdi-test-tube"
                      :readonly="!isInspectionStatus"
                      class="nexus-input"
                    />
                  </v-col>
                </v-row>

                <!-- Row 4: Job Status, Priority, Total Cost, and conditional fields -->
                <v-row>
                  <v-col cols="12" md="3">
                    <v-select
                      v-model="jobStatus"
                      :items="statusOptions"
                      label="Job Status"
                      dense
                      outlined
                      prepend-inner-icon="mdi-information"
                      :rules="[rules.required]"
                      :disabled="checklistRequiredForStatusChange"
                      class="nexus-input"
                      @change="handleStatusChange"
                    />
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-select
                      v-model="priority"
                      :items="priorityOptions"
                      label="Priority"
                      dense
                      outlined
                      prepend-inner-icon="mdi-alert"
                      :rules="[rules.required]"
                      :readonly="!isInspectionStatus"
                      class="nexus-input"
                    />
                  </v-col>
                  
                  <!-- Total Cost - Always Visible -->
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model="totalCost"
                      label="Total Cost"
                      dense
                      outlined
                      prepend-inner-icon="mdi-currency-usd"
                      type="number"
                      :readonly="lockReport"
                      :rules="costingCompleteRules"
                      class="nexus-input"
                    />
                  </v-col>
                  
                  <!-- Quote Number - appears when Quoted -->
                  <v-col cols="12" md="3" v-if="showQuoteNumber">
                    <v-text-field
                      v-model="quoteNumber"
                      label="Quote Number"
                      dense
                      outlined
                      prepend-inner-icon="mdi-file-document-edit"
                      :readonly="lockReport"
                      :rules="quoteNumberRules"
                      class="nexus-input"
                    />
                  </v-col>
                  
                  <!-- SO Number - appears from In Progress onwards, hidden when Completed -->
                  <v-col cols="12" md="3" v-if="showSONumber">
                    <v-text-field
                      v-model="soNumber"
                      label="SO Number"
                      dense
                      outlined
                      prepend-inner-icon="mdi-file-document"
                      :readonly="lockReport"
                      :rules="soNumberRules"
                      class="nexus-input"
                    />
                  </v-col>
                  
                  <!-- Invoice Number - appears only when Completed -->
                  <v-col cols="12" md="3" v-if="showInvoiceNumber">
                    <v-text-field
                      v-model="invoiceNumber"
                      label="Invoice Number"
                      dense
                      outlined
                      prepend-inner-icon="mdi-receipt"
                      :readonly="lockReport"
                      :rules="invoiceNumberRules"
                      class="nexus-input"
                    />
                  </v-col>
                </v-row>

                <!-- Hidden fields for inspection date and date quoted -->
                <input type="hidden" v-model="inspectionDate" />
                <input type="hidden" v-model="dateQuoted" />

                <!-- Job Description -->
                <v-row>
                  <v-col cols="12">
                    <v-textarea
                      v-model="jobDescription"
                      label="Job Description"
                      outlined
                      dense
                      prepend-inner-icon="mdi-text"
                      rows="3"
                      auto-grow
                      :readonly="!isInspectionStatus"
                      class="nexus-input"
                    />
                  </v-col>
                </v-row>

                <!-- INSPECTION CHECKLIST Section -->
                <div class="section-header mb-6 mt-8">
                  <h2 class="section-title">INSPECTION CHECKLIST</h2>
                  <div class="section-divider"></div>
                </div>

                <!-- Checklist Sections -->
                <v-row v-if="checklist.length">
                  <v-col cols="12">
                    <v-card
                      v-for="(section, sectionIndex) in checklist"
                      :key="sectionIndex"
                      class="section-card mb-6"
                    >
                      <!-- Section Header -->
                      <div class="section-header-bar">
                        <h3 class="section-title-text">
                          <v-icon color="white" class="mr-2">mdi-clipboard-check-outline</v-icon>
                          {{ section.sectionTitle }}
                        </h3>
                      </div>

                      <v-card-text class="pa-6">
                        <v-row dense>
                          <!-- Checklist Items -->
                          <v-col
                            cols="12"
                            v-for="(item, index) in section.items"
                            :key="index"
                            class="checklist-item-row"
                          >
                            <v-card class="item-card" :class="{ 'item-card-error': isInspectionStatus && item.checked === null }">
                              <v-card-text class="pa-4">
                                <v-row dense align="center">
                                  <!-- Label Column -->
                                  <v-col cols="12" md="6">
                                    <span class="item-label">{{ item.label }}</span>
                                  </v-col>

                                  <!-- Radio Group Column -->
                                  <v-col cols="12" md="6">
                                    <v-radio-group
                                      v-model="item.checked"
                                      row
                                      dense
                                      class="radio-group-custom"
                                      hide-details
                                      :readonly="!isInspectionStatus"
                                      @change="handleCheckboxChange(item)"
                                    >
                                      <v-radio label="Yes" value="Yes" color="#475569" :disabled="!isInspectionStatus"></v-radio>
                                      <v-radio label="No" value="No" color="#475569" :disabled="!isInspectionStatus"></v-radio>
                                    </v-radio-group>
                                  </v-col>
                                </v-row>

                                <!-- Conditional Text Fields -->
                                <v-row dense v-if="item.checked === 'Yes'" class="mt-2">
                                  <v-col cols="12">
                                    <v-text-field
                                      v-if="item.showseal1"
                                      v-model="item.seal1"
                                      label="Any visible damages"
                                      dense
                                      outlined
                                      :readonly="!isInspectionStatus"
                                      :rules="isInspectionStatus ? [v => !!v || 'Required'] : []"
                                      class="nexus-input mt-2"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showJobNumberField"
                                      v-model="item.jobNumberField"
                                      label="Job Number"
                                      dense
                                      outlined
                                      :readonly="!isInspectionStatus"
                                      :rules="isInspectionStatus ? [v => !!v || 'Required'] : []"
                                      class="nexus-input mt-2"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showseal2"
                                      v-model="item.seal2"
                                      label="Special fitting on seal"
                                      dense
                                      outlined
                                      :readonly="!isInspectionStatus"
                                      :rules="isInspectionStatus ? [v => !!v || 'Required'] : []"
                                      class="nexus-input mt-2"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showseal3"
                                      v-model="item.seal3"
                                      label="Note Previous Job Number"
                                      dense
                                      outlined
                                      :readonly="!isInspectionStatus"
                                      :rules="isInspectionStatus ? [v => !!v || 'Required'] : []"
                                      class="nexus-input mt-2"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showseal4"
                                      v-model="item.seal4"
                                      label="Add photo of anything unusual"
                                      dense
                                      outlined
                                      :readonly="!isInspectionStatus"
                                      :rules="isInspectionStatus ? [v => !!v || 'Required'] : []"
                                      class="nexus-input mt-2"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showsettingclipfield"
                                      v-model="item.settingclipfield"
                                      label="Setting Clip"
                                      dense
                                      outlined
                                      :readonly="!isInspectionStatus"
                                      :rules="isInspectionStatus ? [v => !!v || 'Required'] : []"
                                      class="nexus-input mt-2"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showoringMaterial"
                                      v-model="item.oringMaterial"
                                      label="O-Ring Material"
                                      dense
                                      outlined
                                      :readonly="!isInspectionStatus"
                                      :rules="isInspectionStatus ? [v => !!v || 'Required'] : []"
                                      class="nexus-input mt-2"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showoringdamage"
                                      v-model="item.oringdamage"
                                      label="O-Ring Damages"
                                      dense
                                      outlined
                                      :readonly="!isInspectionStatus"
                                      :rules="isInspectionStatus ? [v => !!v || 'Required'] : []"
                                      class="nexus-input mt-2"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showoringSize"
                                      v-model="item.oringSize"
                                      label="Size"
                                      dense
                                      outlined
                                      :readonly="!isInspectionStatus"
                                      :rules="isInspectionStatus ? [v => !!v || 'Required'] : []"
                                      class="nexus-input mt-2"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showFace1"
                                      v-model="item.Face1"
                                      label="Replace inboard stationary face"
                                      dense
                                      outlined
                                      :readonly="!isInspectionStatus"
                                      :rules="isInspectionStatus ? [v => !!v || 'Required'] : []"
                                      class="nexus-input mt-2"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showFace2"
                                      v-model="item.Face2"
                                      label="Replace inboard Rotary face"
                                      dense
                                      outlined
                                      :readonly="!isInspectionStatus"
                                      :rules="isInspectionStatus ? [v => !!v || 'Required'] : []"
                                      class="nexus-input mt-2"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showFace3"
                                      v-model="item.Face3"
                                      label="Result of vacuum test"
                                      dense
                                      outlined
                                      :readonly="!isInspectionStatus"
                                      :rules="isInspectionStatus ? [v => !!v || 'Required'] : []"
                                      class="nexus-input mt-2"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showFace4"
                                      v-model="item.Face4"
                                      label="Other Damage to faces"
                                      dense
                                      outlined
                                      :readonly="!isInspectionStatus"
                                      :rules="isInspectionStatus ? [v => !!v || 'Required'] : []"
                                      class="nexus-input mt-2"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showOutboardFace1"
                                      v-model="item.OutboardFace1"
                                      label="Replace outboard stationary face"
                                      dense
                                      outlined
                                      :readonly="!isInspectionStatus"
                                      :rules="isInspectionStatus ? [v => !!v || 'Required'] : []"
                                      class="nexus-input mt-2"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showOutboardFace2"
                                      v-model="item.OutboardFace2"
                                      label="Replace outboard Rotary Face"
                                      dense
                                      outlined
                                      :readonly="!isInspectionStatus"
                                      :rules="isInspectionStatus ? [v => !!v || 'Required'] : []"
                                      class="nexus-input mt-2"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showGland1"
                                      v-model="item.Gland1"
                                      label="O-ring surface NOT acceptable"
                                      dense
                                      outlined
                                      :readonly="!isInspectionStatus"
                                      :rules="isInspectionStatus ? [v => !!v || 'Required'] : []"
                                      class="nexus-input mt-2"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showGland2"
                                      v-model="item.Gland2"
                                      label="Damage around setting clip area"
                                      dense
                                      outlined
                                      :readonly="!isInspectionStatus"
                                      :rules="isInspectionStatus ? [v => !!v || 'Required'] : []"
                                      class="nexus-input mt-2"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showGland3"
                                      v-model="item.Gland3"
                                      label="Damage on gasket/o-ring surface"
                                      dense
                                      outlined
                                      :readonly="!isInspectionStatus"
                                      :rules="isInspectionStatus ? [v => !!v || 'Required'] : []"
                                      class="nexus-input mt-2"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showGland4"
                                      v-model="item.Gland4"
                                      label="Pitting seen on gland"
                                      dense
                                      outlined
                                      :readonly="!isInspectionStatus"
                                      :rules="isInspectionStatus ? [v => !!v || 'Required'] : []"
                                      class="nexus-input mt-2"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showGland5"
                                      v-model="item.Gland5"
                                      label="Gland Port Size"
                                      dense
                                      outlined
                                      :readonly="!isInspectionStatus"
                                      :rules="isInspectionStatus ? [v => !!v || 'Required'] : []"
                                      class="nexus-input mt-2"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showGland6"
                                      v-model="item.Gland6"
                                      label="Spring holes damaged"
                                      dense
                                      outlined
                                      :readonly="!isInspectionStatus"
                                      :rules="isInspectionStatus ? [v => !!v || 'Required'] : []"
                                      class="nexus-input mt-2"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showGland7"
                                      v-model="item.Gland7"
                                      label="Dents/external damage"
                                      dense
                                      outlined
                                      :readonly="!isInspectionStatus"
                                      :rules="isInspectionStatus ? [v => !!v || 'Required'] : []"
                                      class="nexus-input mt-2"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showGland8"
                                      v-model="item.Gland8"
                                      label="Pins Bent loose, worn, or missing"
                                      dense
                                      outlined
                                      :readonly="!isInspectionStatus"
                                      :rules="isInspectionStatus ? [v => !!v || 'Required'] : []"
                                      class="nexus-input mt-2"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showGland9"
                                      v-model="item.Gland9"
                                      label="Other"
                                      dense
                                      outlined
                                      :readonly="!isInspectionStatus"
                                      :rules="isInspectionStatus ? [v => !!v || 'Required'] : []"
                                      class="nexus-input mt-2"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showadd1"
                                      v-model="item.add1"
                                      label="Lip Seals"
                                      dense
                                      outlined
                                      :readonly="!isInspectionStatus"
                                      :rules="isInspectionStatus ? [v => !!v || 'Required'] : []"
                                      class="nexus-input mt-2"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showadd2"
                                      v-model="item.add2"
                                      label="Tool Required 1"
                                      dense
                                      outlined
                                      :readonly="!isInspectionStatus"
                                      :rules="isInspectionStatus ? [v => !!v || 'Required'] : []"
                                      class="nexus-input mt-2"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showadd3"
                                      v-model="item.add3"
                                      label="Tool Required 2"
                                      dense
                                      outlined
                                      :readonly="!isInspectionStatus"
                                      :rules="isInspectionStatus ? [v => !!v || 'Required'] : []"
                                      class="nexus-input mt-2"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showadd4"
                                      v-model="item.add4"
                                      label="Tool Required 3"
                                      dense
                                      outlined
                                      :readonly="!isInspectionStatus"
                                      :rules="isInspectionStatus ? [v => !!v || 'Required'] : []"
                                      class="nexus-input mt-2"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showadd5"
                                      v-model="item.add5"
                                      label="Additional Part 1"
                                      dense
                                      outlined
                                      :readonly="!isInspectionStatus"
                                      :rules="isInspectionStatus ? [v => !!v || 'Required'] : []"
                                      class="nexus-input mt-2"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showadd6"
                                      v-model="item.add6"
                                      label="Additional Part 2"
                                      dense
                                      outlined
                                      :readonly="!isInspectionStatus"
                                      :rules="isInspectionStatus ? [v => !!v || 'Required'] : []"
                                      class="nexus-input mt-2"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showadd7"
                                      v-model="item.add7"
                                      label="Metal Bellows"
                                      dense
                                      outlined
                                      :readonly="!isInspectionStatus"
                                      :rules="isInspectionStatus ? [v => !!v || 'Required'] : []"
                                      class="nexus-input mt-2"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showcheck1"
                                      v-model="item.check1"
                                      label="Ovality on sealing surface"
                                      dense
                                      outlined
                                      :readonly="!isInspectionStatus"
                                      :rules="isInspectionStatus ? [v => !!v || 'Required'] : []"
                                      class="nexus-input mt-2"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showcheck2"
                                      v-model="item.check2"
                                      label="Damage on Other Parts"
                                      dense
                                      outlined
                                      :readonly="!isInspectionStatus"
                                      :rules="isInspectionStatus ? [v => !!v || 'Required'] : []"
                                      class="nexus-input mt-2"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showcheck3"
                                      v-model="item.check3"
                                      label="Additional parts"
                                      dense
                                      outlined
                                      :readonly="!isInspectionStatus"
                                      :rules="isInspectionStatus ? [v => !!v || 'Required'] : []"
                                      class="nexus-input mt-2"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showcheck4"
                                      v-model="item.check4"
                                      label="Machining to be Done"
                                      dense
                                      outlined
                                      :readonly="!isInspectionStatus"
                                      :rules="isInspectionStatus ? [v => !!v || 'Required'] : []"
                                      class="nexus-input mt-2"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showcheck6"
                                      v-model="item.check6"
                                      label="Other parts and components"
                                      dense
                                      outlined
                                      :readonly="!isInspectionStatus"
                                      :rules="isInspectionStatus ? [v => !!v || 'Required'] : []"
                                      class="nexus-input mt-2"
                                    ></v-text-field>
                                  </v-col>
                                </v-row>
                              </v-card-text>
                            </v-card>
                          </v-col>

                          <!-- Upload and View Photo Buttons -->
                          <v-col cols="12" class="d-flex justify-start mt-4">
                            <v-btn 
                              v-if="isInspectionStatus"
                              class="nexus-btn mr-3" 
                              @click="openUploadOptionsDialog(sectionIndex)"
                            >
                              <v-icon left>mdi-camera</v-icon>Upload Photo
                            </v-btn>

                            <v-btn
                              v-if="section.photos && section.photos.length"
                              color="success"
                              class="nexus-btn-secondary"
                              @click="openPhotoDialog(section.photos, section.sectionTitle)"
                            >
                              <v-icon left>mdi-eye</v-icon>View Photos ({{ section.photos.length }})
                            </v-btn>
                          </v-col>

                          <!-- Upload Loader -->
                          <v-col cols="12" v-if="uploadingPhoto">
                            <v-progress-linear indeterminate color="#475569" class="mb-2"></v-progress-linear>
                            <span class="uploading-text">Uploading photos...</span>
                          </v-col>

                          <!-- Upload Photos Input -->
                          <input
                            :ref="'fileInput' + sectionIndex"
                            type="file"
                            accept="image/*"
                            multiple
                            @change="handleFileUpload($event, sectionIndex)"
                            style="display: none"
                          />
                        </v-row>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>

                <!-- Comment and Summary Section -->
                <v-row>
                  <v-col cols="12">
                    <v-textarea
                      v-model="comment"
                      label="Comment (For Internal Workshop Only)"
                      outlined
                      dense
                      prepend-inner-icon="mdi-comment-text"
                      rows="3"
                      auto-grow
                      :readonly="!isInspectionStatus"
                      :rules="[rules.required]"
                      class="nexus-input"
                    />
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12">
                    <v-textarea
                      v-model="jobSummary"
                      label="Summary (Short Description to Send to Customer)"
                      outlined
                      dense
                      prepend-inner-icon="mdi-text-box"
                      rows="3"
                      auto-grow
                      :readonly="!isInspectionStatus"
                      :rules="[rules.required]"
                      class="nexus-input"
                    />
                  </v-col>
                </v-row>

                <!-- Action Buttons -->
                <v-row justify="center" class="my-6">
                  <v-btn 
                    large 
                    color="primary" 
                    class="nexus-btn mr-4" 
                    @click="confirmSubmit" 
                    :disabled="!valid || loading || lockReport || saving"
                  >
                    <v-icon left>mdi-content-save</v-icon>
                    {{ saving ? 'Saving Changes...' : 'Save Changes' }}
                  </v-btn>

                  <v-btn 
                    large 
                    color="success" 
                    class="nexus-btn" 
                    @click="downloadReport" 
                    :disabled="loading || generating"
                  >
                    <v-icon left>mdi-download</v-icon>
                    {{ generating ? 'Generating PDF...' : 'Download Report' }}
                  </v-btn>
                </v-row>
              </v-form>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Photo Dialog -->
    <v-dialog v-model="photoDialog" max-width="800px">
      <v-card class="nexus-dialog">
        <v-toolbar dark flat class="photo-dialog-header">
          <v-toolbar-title>{{ currentPhotoLabel }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="closePhotoDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-carousel v-model="carouselIndex" height="600">
          <v-carousel-item v-for="(photo, index) in selectedPhotos" :key="index" :src="photo"></v-carousel-item>
        </v-carousel>
      </v-card>
    </v-dialog>

    <!-- Upload Options Dialog -->
    <v-dialog v-model="uploadOptionsDialog" max-width="500px">
      <v-card class="nexus-dialog">
        <v-card-title class="nexus-dialog-title">
          <v-icon left color="#475569">mdi-camera</v-icon>
          Select Photo Upload Method
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-6">
          <v-row>
            <v-col cols="12">
              <v-btn class="upload-option-btn" @click="takePhoto" block large>
                <v-icon left>mdi-camera</v-icon>
                Use Camera
              </v-btn>
            </v-col>
            <v-col cols="12">
              <v-btn class="upload-option-btn" @click="uploadFromGallery" block large>
                <v-icon left>mdi-image</v-icon>
                Upload from Gallery
              </v-btn>
            </v-col>
            <v-col cols="12">
              <v-btn class="upload-option-btn" @click="uploadFromFiles" block large>
                <v-icon left>mdi-folder</v-icon>
                Upload from Files
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn text @click="closeUploadOptionsDialog">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Confirmation Dialog -->
    <v-dialog v-model="confirmationDialog.visible" max-width="400">
      <v-card class="nexus-dialog">
        <v-card-title class="nexus-dialog-title">{{ confirmationDialog.title }}</v-card-title>
        <v-card-text>{{ confirmationDialog.message }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="success" text @click="confirmationDialog.confirm">Yes</v-btn>
          <v-btn color="grey" text @click="confirmationDialog.visible = false">No</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Success Snackbar -->
    <v-snackbar v-model="snackbar" color="success" timeout="3000">
      Report updated successfully!
      <v-btn color="white" text @click="snackbar = false">Close</v-btn>
    </v-snackbar>

    <!-- Error Snackbar -->
    <v-snackbar v-model="errorSnackbar" color="error" timeout="3000">
      {{ errorMessage }}
      <v-btn color="white" text @click="errorSnackbar = false">Close</v-btn>
    </v-snackbar>

    <!-- Loading Overlay for PDF Generation -->
    <v-overlay :value="generating">
      <v-progress-circular indeterminate size="70" color="white"></v-progress-circular>
      <div class="mt-4 white--text">Generating PDF Report...</div>
    </v-overlay>
  </div>
</template>

<script>
import {
  getFirestore,
  doc,
  getDoc,
  updateDoc
} from "firebase/firestore";
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL, getBlob } from 'firebase/storage';
import { firebaseApp, auth } from "@/firebaseConfig";
import { onAuthStateChanged } from "firebase/auth";
import jsPDF from "jspdf";

// Import the Nexus logo
import NexusLogo from '@/assets/pdflogo.jpg';

const db = getFirestore(firebaseApp);
const storage = getStorage(firebaseApp);

export default {
  name: "EditJobReport",
  data() {
    return {
      loading: false,
      generating: false,
      saving: false,
      valid: false,
      
      // Job ID
      selectedJobId: null,
      
      // Job data
      jobNumber: "",
      customerName: "",
      contactPerson: "",
      customerReference: "",
      sealType: "",
      serialNumber: "",
      shaftSize: "",
      engineer: "",
      technician: "",
      salesperson: "",
      jobStatus: "",
      originalStatus: "",
      inspectionDate: "",
      dateQuoted: "",
      priority: "",
      jobDescription: "",
      
      // Conditional Document Fields
      soNumber: "",
      quoteNumber: "",
      invoiceNumber: "",
      sampleNumber: "",
      
      // Total Cost and Lead Time
      totalCost: "",
      TotalLead: "",
      
      // Comment and Summary
      comment: "",
      jobSummary: "",
      
      // Checklist data
      checklist: [],
      checklistFilled: false,
      
      // Report notes
      reportNotes: "",
      
      // Photo upload
      uploadingPhoto: false,
      uploadOptionsDialog: false,
      selectedSectionIndex: null,
      
      // Photo dialog
      photoDialog: false,
      selectedPhotos: [],
      currentPhotoLabel: "",
      carouselIndex: 0,
      
      // Dialogs
      confirmationDialog: {
        visible: false,
        title: "",
        message: "",
        confirm: null
      },
      
      // Snackbar
      snackbar: false,
      errorSnackbar: false,
      errorMessage: "",
      
      // User
      loggedInUser: "",
      currentUser: null,
      isAdmin: false,
      
      // Dropdown options
      priorityOptions: ['Low', 'Medium', 'High'],
      engineerOptions: [],
      technicianOptions: [],
      salespersonOptions: [],
      leadTimeOptions: [],
      
      // Status options
      statuses: [
        "Inspection",
        "Costing Complete",
        "Quoted",
        "In Progress",
        "Ready",
        "Completed",
        "Delayed",
        "Canceled"
      ],

      // Validation Rules
      rules: {
        required: (v) => !!v || 'Required.',
      },
    };
  },

  computed: {
    lockReport() {
      return this.originalStatus === "Completed" || this.originalStatus === "Canceled";
    },
    
    isInspectionStatus() {
      return this.jobStatus === "Inspection" && !this.lockReport;
    },
    
    checklistRequiredForStatusChange() {
      return this.jobStatus === "Inspection" && !this.checklistFilled;
    },
    
    statusOptions() {
      return this.statuses.map((s, idx) => {
        let disabled = false;
        
        if (this.jobStatus === "Inspection" && !this.checklistFilled && s !== "Inspection") {
          disabled = true;
        }
        else if (!this.isAdmin) {
          const currentIndex = this.statuses.indexOf(this.originalStatus);
          
          if (s === this.originalStatus) {
            disabled = false;
          } else if (["Canceled", "Delayed"].includes(s)) {
            disabled = false;
          } else if (idx === currentIndex + 1) {
            if (this.originalStatus === "Inspection" && s === "Costing Complete" && !this.checklistFilled) {
              disabled = true;
            } else {
              disabled = false;
            }
          } else {
            disabled = true;
          }
        }
        
        return { text: s, value: s, disabled };
      });
    },
    
    showSONumber() {
      return ['In Progress', 'Ready', 'Delayed'].includes(this.jobStatus);
    },
    
    showQuoteNumber() {
      return this.jobStatus === 'Quoted';
    },
    
    showInvoiceNumber() {
      return this.jobStatus === 'Completed';
    },

    costingCompleteRules() {
      if (this.jobStatus === 'Costing Complete') {
        return [this.rules.required];
      }
      return [];
    },

    quoteNumberRules() {
      if (this.showQuoteNumber) {
        return [this.rules.required];
      }
      return [];
    },

    soNumberRules() {
      if (this.showSONumber) {
        return [this.rules.required];
      }
      return [];
    },

    invoiceNumberRules() {
      if (this.showInvoiceNumber) {
        return [this.rules.required];
      }
      return [];
    },
  },

  watch: {
    jobStatus(newStatus, oldStatus) {
      if (newStatus === 'Costing Complete' && !this.totalCost) {
        this.showError('Total Cost must be filled before changing to Costing Complete');
        this.$nextTick(() => {
          this.jobStatus = oldStatus;
        });
      }
    },

    checklist: {
      handler() {
        this.checkChecklistCompletion();
      },
      deep: true
    }
  },

  async created() {
    this.loading = true;
    this.selectedJobId = localStorage.getItem('selectedJobReportId') || '';

    onAuthStateChanged(auth, async (user) => {
      if (!user) {
        this.$router.push('/login');
        return;
      }
      
      try {
        this.loggedInUser = user.email;
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          this.currentUser = userDoc.data();
          this.isAdmin = this.currentUser.role === 'Admin';
        }
      } catch (e) {
        console.error("Error loading user:", e);
      }
    });

    if (this.selectedJobId) {
      await Promise.all([
        this.loadJobReport(),
        this.fetchDropdownOptions()
      ]);
      this.checkChecklistCompletion();
    }
    
    this.loading = false;
  },

  methods: {
    async loadJobReport() {
      try {
        const reportDoc = await getDoc(doc(db, "inspectionReports", this.selectedJobId));
        
        if (reportDoc.exists()) {
          const report = reportDoc.data();
          this.jobNumber = report.jobNumber || "";
          this.customerName = report.customerName || "";
          this.contactPerson = report.contactPerson || "";
          this.customerReference = report.customerReference || "";
          this.sealType = report.sealType || "";
          this.serialNumber = report.serialNumber || "";
          this.shaftSize = report.shaftSize || "";
          this.engineer = report.engineer || "";
          this.technician = report.technician || "";
          this.salesperson = report.salesperson || "";
          this.jobStatus = report.jobStatus || "";
          this.originalStatus = report.jobStatus || "";
          this.inspectionDate = report.inspectionDate || "";
          this.dateQuoted = report.dateQuoted || "";
          this.priority = report.priority || "";
          this.jobDescription = report.jobDescription || "";
          this.reportNotes = report.reportNotes || "";
          this.soNumber = report.soNumber || "";
          this.quoteNumber = report.quoteNumber || "";
          this.invoiceNumber = report.invoiceNumber || "";
          this.sampleNumber = report.sampleNumber || "";
          this.totalCost = report.totalCost || "";
          this.TotalLead = report.TotalLead || "";
          this.comment = report.comment || "";
          this.jobSummary = report.jobSummary || "";
          this.checklist = report.checklist || [];
        }
      } catch (error) {
        console.error("Error loading job report:", error);
        this.showError("Failed to load job report");
      }
    },

    async fetchDropdownOptions() {
      try {
        const dropdownDoc = await getDoc(doc(db, 'admin', 'dropdownManager'));
        
        if (dropdownDoc.exists()) {
          const data = dropdownDoc.data();
          this.engineerOptions = data.engineers || [];
          this.technicianOptions = data.technicians || [];
          this.salespersonOptions = data.salespersons || [];
          this.leadTimeOptions = data.leadTimes || [];
        } else {
          this.showError('Dropdown configuration not found.');
        }
      } catch (error) {
        console.error("Error fetching dropdown options:", error);
        this.showError('Failed to fetch dropdown options.');
      }
    },

    checkChecklistCompletion() {
      if (!this.checklist || this.checklist.length === 0) {
        this.checklistFilled = false;
        return;
      }
      
      let allAnswered = true;
      
      for (const section of this.checklist) {
        if (section.items && section.items.length > 0) {
          for (const item of section.items) {
            if (!item.checked || (item.checked !== 'Yes' && item.checked !== 'No')) {
              allAnswered = false;
              break;
            }
            
            if (item.checked === 'Yes') {
              if (item.showseal1 && !item.seal1) allAnswered = false;
              if (item.showJobNumberField && !item.jobNumberField) allAnswered = false;
              if (item.showseal2 && !item.seal2) allAnswered = false;
              if (item.showseal3 && !item.seal3) allAnswered = false;
              if (item.showseal4 && !item.seal4) allAnswered = false;
              if (item.showsettingclipfield && !item.settingclipfield) allAnswered = false;
              if (item.showoringMaterial && !item.oringMaterial) allAnswered = false;
              if (item.showoringdamage && !item.oringdamage) allAnswered = false;
              if (item.showoringSize && !item.oringSize) allAnswered = false;
              if (item.showFace1 && !item.Face1) allAnswered = false;
              if (item.showFace2 && !item.Face2) allAnswered = false;
              if (item.showFace3 && !item.Face3) allAnswered = false;
              if (item.showFace4 && !item.Face4) allAnswered = false;
              if (item.showOutboardFace1 && !item.OutboardFace1) allAnswered = false;
              if (item.showOutboardFace2 && !item.OutboardFace2) allAnswered = false;
              if (item.showGland1 && !item.Gland1) allAnswered = false;
              if (item.showGland2 && !item.Gland2) allAnswered = false;
              if (item.showGland3 && !item.Gland3) allAnswered = false;
              if (item.showGland4 && !item.Gland4) allAnswered = false;
              if (item.showGland5 && !item.Gland5) allAnswered = false;
              if (item.showGland6 && !item.Gland6) allAnswered = false;
              if (item.showGland7 && !item.Gland7) allAnswered = false;
              if (item.showGland8 && !item.Gland8) allAnswered = false;
              if (item.showGland9 && !item.Gland9) allAnswered = false;
              if (item.showadd1 && !item.add1) allAnswered = false;
              if (item.showadd2 && !item.add2) allAnswered = false;
              if (item.showadd3 && !item.add3) allAnswered = false;
              if (item.showadd4 && !item.add4) allAnswered = false;
              if (item.showadd5 && !item.add5) allAnswered = false;
              if (item.showadd6 && !item.add6) allAnswered = false;
              if (item.showadd7 && !item.add7) allAnswered = false;
              if (item.showcheck1 && !item.check1) allAnswered = false;
              if (item.showcheck2 && !item.check2) allAnswered = false;
              if (item.showcheck3 && !item.check3) allAnswered = false;
              if (item.showcheck4 && !item.check4) allAnswered = false;
              if (item.showcheck6 && !item.check6) allAnswered = false;
            }
            
            if (!allAnswered) break;
          }
        }
        if (!allAnswered) break;
      }
      
      this.checklistFilled = allAnswered;
    },

    handleCheckboxChange(item) {
      if (item.checked === 'No') {
        this.clearDependentFields(item);
      } else if (item.checked === 'Yes') {
        this.toggleFieldVisibility(item);
      }
      this.checkChecklistCompletion();
    },

    clearDependentFields(item) {
      for (const key in item) {
        if (key.startsWith('show')) {
          this.$set(item, key, false);
        }
        if (
          key.match(
            /^(seal|Gland|add|check|Face|OutboardFace|oring|settingclipfield|jobNumberField)$/
          )
        ) {
          if (typeof item[key] === 'string') {
            this.$set(item, key, '');
          } else if (typeof item[key] === 'boolean') {
            this.$set(item, key, false);
          }
        }
      }
    },

    toggleFieldVisibility(item) {
      const label = item.label;
      const visibilityMap = {
        'Any visible damage?': 'showseal1',
        'Special Fittings on seal?': 'showseal2',
        'Note Previous Job Number': 'showJobNumberField',
        'Add Photo of anything unusual': 'showseal3',
        'Setting Clips reusable': 'showsettingclipfield',
        'O-ring Material': 'showoringMaterial',
        'O-ring damages': 'showoringdamage',
        'O-ring Sizes': 'showoringSize',
        'Replace inboard stationary face': 'showFace1',
        'Replace inboard Rotary face': 'showFace2',
        'Result of Vacuum test (if required)': ['showFace3', 'showOutboardFace3'],
        'Other Damage on faces': ['showFace4', 'showOutboardFace4'],
        'Replace outboard stationary face': 'showOutboardFace1',
        'Replace outboard Rotary face': 'showOutboardFace2',
        'O-ring surfaces acceptable': 'showGland1',
        'Damage around setting clip area': 'showGland2',
        'Damage on gasket/o-ring surface': 'showGland3',
        'Pitting seen on gland': 'showGland4',
        'Gland port sizes': 'showGland5',
        'Spring holes damaged': 'showGland6',
        'Dents/external damage': 'showGland7',
        'Pins: Bent, Loose, worn, missing': 'showGland8',
        Other: 'showGland9',
        'Lip seals': 'showadd1',
        'Tool required 1': 'showadd2',
        'Tool required 2': 'showadd3',
        'Tool required 3': 'showadd4',
        'Additional part 1': 'showadd5',
        'Additional part 2': 'showadd6',
        'Metal bellows': 'showadd7',
        'Ovality on sealing surfaces': 'showcheck1',
        'Damage on other parts': 'showcheck2',
        'Additional parts (Shaft sleeve, Backplate, etc.)': 'showcheck3',
        'Note of machining to be done': 'showcheck4',
        'Other parts or components': 'showcheck6',
      };

      if (visibilityMap[label]) {
        const fields = Array.isArray(visibilityMap[label])
          ? visibilityMap[label]
          : [visibilityMap[label]];
        fields.forEach((field) => {
          this.$set(item, field, item.checked === 'Yes');
        });
      }
    },

    openUploadOptionsDialog(sectionIndex) {
      this.selectedSectionIndex = sectionIndex;
      this.uploadOptionsDialog = true;
    },

    closeUploadOptionsDialog() {
      this.uploadOptionsDialog = false;
    },

    async takePhoto() {
      this.closeUploadOptionsDialog();
      const fileInput = this.$refs[`fileInput${this.selectedSectionIndex}`][0];
      fileInput.setAttribute('capture', 'camera');
      fileInput.click();
    },

    uploadFromGallery() {
      this.closeUploadOptionsDialog();
      const fileInput = this.$refs[`fileInput${this.selectedSectionIndex}`][0];
      fileInput.removeAttribute('capture');
      fileInput.setAttribute('accept', 'image/*');
      fileInput.click();
    },

    uploadFromFiles() {
      this.closeUploadOptionsDialog();
      const fileInput = this.$refs[`fileInput${this.selectedSectionIndex}`][0];
      fileInput.removeAttribute('capture');
      fileInput.setAttribute('accept', '*/*');
      fileInput.click();
    },

    async handleFileUpload(event, sectionIndex) {
      const files = event.target.files;
      const photos = [];

      this.uploadingPhoto = true;

      try {
        for (const file of files) {
          const uniqueFileName = `${Date.now()}_${file.name}`;
          const storageReference = storageRef(
            storage,
            `inspectionReports/photos/${uniqueFileName}`
          );
          const snapshot = await uploadBytes(storageReference, file);
          const downloadURL = await getDownloadURL(snapshot.ref);
          photos.push(downloadURL);
        }
        this.checklist[sectionIndex].photos.push(...photos);
      } catch (error) {
        this.showError('Failed to upload photos.');
      } finally {
        this.uploadingPhoto = false;
      }
    },

    handleStatusChange() {
      if (this.jobStatus === 'Quoted' && !this.dateQuoted) {
        this.dateQuoted = new Date().toISOString().split('T')[0];
      }

      if (this.jobStatus !== 'Quoted') {
        this.quoteNumber = "";
      }
      
      if (this.jobStatus === 'Completed') {
        this.soNumber = "";
      }
      
      if (this.jobStatus !== 'Completed') {
        this.invoiceNumber = "";
      }
    },

    openPhotoDialog(photos, label) {
      this.selectedPhotos = photos;
      this.currentPhotoLabel = label;
      this.carouselIndex = 0;
      this.photoDialog = true;
    },

    closePhotoDialog() {
      this.photoDialog = false;
    },

   async downloadReport() {
  this.generating = true;
  
  try {
    const pdf = new jsPDF("p", "mm", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 15;
    let yPosition = 15;

    const checkPageBreak = (requiredSpace) => {
      if (yPosition + requiredSpace > pageHeight - margin) {
        pdf.addPage();
        yPosition = margin;
        return true;
      }
      return false;
    };

    // Header section
    pdf.setFillColor(240, 240, 240);
    pdf.rect(margin, yPosition, pageWidth - 2 * margin, 20, 'F');
    
    try {
      const logoWidth = 50;
      const logoHeight = 15;
      const logoX = margin + 5;
      const logoY = yPosition + 2.5;
      pdf.addImage(NexusLogo, 'PNG', logoX, logoY, logoWidth, logoHeight);
    } catch (error) {
      console.warn('Could not add logo:', error);
      pdf.setFontSize(12);
      pdf.setTextColor(60, 60, 60);
      pdf.setFont(undefined, 'bold');
      pdf.text('Nexus Seals', margin + 5, yPosition + 10);
    }
    
    pdf.setFontSize(18);
    pdf.setTextColor(220, 53, 69);
    pdf.setFont(undefined, 'bold');
    pdf.text(`Job #${this.jobNumber} Report`, pageWidth - margin - 5, yPosition + 12, { align: 'right' });
    
    yPosition += 25;
    pdf.setFontSize(10);
    
    const infoFields = [
      { label: 'Customer:', value: this.customerName || '-' },
      { label: 'Type:', value: this.sealType || '-' },
      { label: 'Size:', value: this.shaftSize ? `${this.shaftSize} mm` : '-' },
      { label: 'Materials:', value: '-' },
      { label: 'Cust Ref:', value: this.customerReference || '-' },
      { label: 'Summary:', value: this.jobSummary || '-' }
    ];

    infoFields.forEach(field => {
      pdf.setTextColor(220, 53, 69);
      pdf.setFont(undefined, 'bold');
      pdf.text(field.label, margin, yPosition);
      
      pdf.setTextColor(0, 0, 0);
      pdf.setFont(undefined, 'normal');
      const lines = pdf.splitTextToSize(field.value, pageWidth - margin - 40);
      pdf.text(lines, margin + 35, yPosition);
      
      yPosition += Math.max(5, lines.length * 5);
    });

    yPosition += 5;

    // Collect all photos from checklist
    const allPhotos = [];
    this.checklist.forEach(section => {
      if (section.photos && section.photos.length > 0) {
        allPhotos.push(...section.photos);
      }
    });

    console.log('Total photos found:', allPhotos.length);
    console.log('Photo URLs:', allPhotos);

    if (allPhotos.length > 0) {
      checkPageBreak(15);
      
      pdf.setFillColor(220, 53, 69);
      pdf.rect(margin, yPosition, pageWidth - 2 * margin, 7, 'F');
      pdf.setFontSize(11);
      pdf.setTextColor(255, 255, 255);
      pdf.setFont(undefined, 'bold');
      pdf.text('Job Report Photos', margin + 2, yPosition + 5);
      
      yPosition += 12;

      // Load images using Image element with canvas conversion
      const imagePromises = allPhotos.map((photoUrl, index) => {
        return new Promise((resolve) => {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          
          img.onload = () => {
            try {
              console.log(`Image ${index + 1} loaded successfully`);
              const canvas = document.createElement('canvas');
              canvas.width = img.width;
              canvas.height = img.height;
              const ctx = canvas.getContext('2d');
              ctx.drawImage(img, 0, 0);
              const base64 = canvas.toDataURL('image/jpeg', 0.8);
              console.log(`Image ${index + 1} converted to base64`);
              resolve(base64);
            } catch (error) {
              console.error(`Error converting image ${index + 1} to base64:`, error);
              resolve(null);
            }
          };
          
          img.onerror = (error) => {
            console.error(`Error loading image ${index + 1}:`, error);
            console.error(`Failed URL:`, photoUrl);
            resolve(null);
          };
          
          // Use the URL directly with token
          img.src = photoUrl;
        });
      });

      const images = await Promise.all(imagePromises);
      const validImages = images.filter(img => img !== null);
      
      console.log('Valid images loaded:', validImages.length, 'out of', allPhotos.length);

      if (validImages.length === 0) {
        pdf.setFontSize(10);
        pdf.setTextColor(100, 100, 100);
        pdf.text('Photos could not be loaded. Please check Firebase Storage CORS settings.', margin, yPosition);
        yPosition += 10;
      } else {
        const imagesPerRow = 3;
        const imgWidth = 55;
        const imgHeight = 41;
        const spacingX = 5;
        const spacingY = 5;
        const availableWidth = pageWidth - 2 * margin;
        const totalImgWidth = imagesPerRow * imgWidth + (imagesPerRow - 1) * spacingX;
        const startX = margin + (availableWidth - totalImgWidth) / 2;

        let rowOnCurrentPage = 0;
        
        for (let i = 0; i < validImages.length; i++) {
          const col = i % imagesPerRow;
          
          if (col === 0 && i > 0) {
            const nextRowY = yPosition + (rowOnCurrentPage + 1) * (imgHeight + spacingY);
            if (nextRowY + imgHeight > pageHeight - margin - 15) {
              pdf.addPage();
              yPosition = margin;
              rowOnCurrentPage = 0;
            } else {
              rowOnCurrentPage++;
            }
          }

          const xPos = startX + col * (imgWidth + spacingX);
          const currentYPos = yPosition + rowOnCurrentPage * (imgHeight + spacingY);

          try {
            console.log(`Adding image ${i + 1} to PDF at position (${xPos}, ${currentYPos})`);
            pdf.addImage(validImages[i], 'JPEG', xPos, currentYPos, imgWidth, imgHeight);
            console.log(`Successfully added image ${i + 1} to PDF`);
          } catch (imgError) {
            console.error(`Failed to add image ${i + 1} to PDF:`, imgError);
          }
        }

        yPosition += (rowOnCurrentPage + 1) * (imgHeight + spacingY);
      }
    }

    // Add page numbers
    const pageCount = pdf.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      pdf.setPage(i);
      pdf.setFontSize(9);
      pdf.setTextColor(128, 128, 128);
      pdf.text(`Page ${i} of ${pageCount}`, pageWidth - margin - 20, pageHeight - 10);
    }

    pdf.save(`JobReport-${this.jobNumber}.pdf`);
    this.showSuccess("PDF downloaded successfully!");
  } catch (error) {
    console.error("Error generating PDF:", error);
    this.showError("Failed to generate PDF: " + error.message);
  } finally {
    this.generating = false;
  }
},

    confirmSubmit() {
      if (!this.$refs.form.validate()) {
        this.showError('Please fill in all required fields correctly.');
        return;
      }

      if (this.jobStatus === 'Costing Complete' && !this.totalCost) {
        this.showError('Total Cost must be filled before changing to Costing Complete');
        return;
      }

      this.confirmationDialog = {
        visible: true,
        title: "Confirm Save",
        message: "Save changes to this job report?",
        confirm: this.submitForm
      };
    },

    async submitForm() {
      if (!this.$refs.form.validate() || this.saving) return;
      
      this.saving = true;
      this.loading = true;
      
      try {
        const updateData = {
          jobStatus: this.jobStatus,
          priority: this.priority,
          reportNotes: this.reportNotes,
          totalCost: this.totalCost,
          dateQuoted: this.dateQuoted,
          customerReference: this.customerReference,
          sealType: this.sealType,
          serialNumber: this.serialNumber,
          shaftSize: this.shaftSize,
          TotalLead: this.TotalLead,
          engineer: this.engineer,
          technician: this.technician,
          salesperson: this.salesperson,
          sampleNumber: this.sampleNumber,
          jobDescription: this.jobDescription,
          comment: this.comment,
          jobSummary: this.jobSummary,
          checklist: this.checklist,
          updatedDate: new Date().toISOString(),
          updatedBy: this.loggedInUser
        };

        if (this.showQuoteNumber) {
          updateData.quoteNumber = this.quoteNumber;
        } else {
          updateData.quoteNumber = null;
        }

        if (this.showSONumber) {
          updateData.soNumber = this.soNumber;
        } else {
          updateData.soNumber = null;
        }

        if (this.showInvoiceNumber) {
          updateData.invoiceNumber = this.invoiceNumber;
        } else {
          updateData.invoiceNumber = null;
        }

        if (this.jobStatus === 'In Progress' && this.originalStatus !== 'In Progress') {
          updateData.inProgressDate = new Date().toISOString();
        }

        await updateDoc(doc(db, 'inspectionReports', this.selectedJobId), updateData);
        
        this.originalStatus = this.jobStatus;
        this.snackbar = true;
      } catch (error) {
        console.error("Error updating report:", error);
        this.showError(`Failed to update report: ${error.message}`);
      } finally {
        this.loading = false;
        this.saving = false;
        this.confirmationDialog.visible = false;
      }
    },

    confirmBackNavigation() {
      this.confirmationDialog = {
        visible: true,
        title: "Confirm Navigation",
        message: "Are you sure you want to go back? Any unsaved changes will be lost.",
        confirm: this.goBack
      };
    },

    goBack() {
      this.confirmationDialog.visible = false;
      this.$router.go(-1);
    },

    showSuccess(message) {
      this.snackbar = true;
    },

    showError(msg) {
      this.errorMessage = msg;
      this.errorSnackbar = true;
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');

.inspection-report-container {
  background: linear-gradient(135deg, #475569 0%, #334155 100%);
  min-height: 100vh;
  font-family: 'Inter', sans-serif;
}

.page-title {
  color: white;
  font-weight: 700;
  font-size: 2.5rem;
  text-align: center;
  letter-spacing: 2px;
  margin: 0;
}

.main-card {
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 20px 40px -12px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1);
  margin-top: 20px;
  transition: all 0.3s ease;
}

.section-header {
  display: flex;
  align-items: center;
  margin-bottom: 24px;
}

.section-title {
  color: #475569;
  font-weight: 600;
  font-size: 1.25rem;
  letter-spacing: 1px;
  margin: 0;
  white-space: nowrap;
}

.section-divider {
  flex: 1;
  height: 2px;
  background: linear-gradient(90deg, #475569 0%, rgba(71, 85, 105, 0.2) 100%);
  margin-left: 20px;
  border-radius: 1px;
}

.nexus-input >>> .v-input__control {
  border-radius: 12px;
}

.nexus-input >>> .v-input__slot {
  background: rgba(248, 250, 252, 0.8) !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 12px !important;
  transition: all 0.3s ease;
}

.nexus-input >>> .v-input__slot:hover {
  border-color: #475569 !important;
  background: rgba(255, 255, 255, 0.9) !important;
}

.nexus-input >>> .v-input--is-focused .v-input__slot {
  border-color: #475569 !important;
  background: rgba(255, 255, 255, 1) !important;
  box-shadow: 0 0 0 3px rgba(71, 85, 105, 0.15);
}

.section-card {
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 20px 40px -12px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1);
  overflow: hidden;
  transition: all 0.3s ease;
}

.section-header-bar {
  background: linear-gradient(135deg, #475569 0%, #334155 100%);
  padding: 16px 24px;
  display: flex;
  align-items: center;
}

.section-title-text {
  color: white;
  font-weight: 600;
  font-size: 1.25rem;
  letter-spacing: 1px;
  display: flex;
  align-items: center;
  margin: 0;
}

.checklist-item-row {
  margin-bottom: 12px;
}

.item-card {
  background: rgba(248, 250, 252, 0.8);
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  transition: all 0.3s ease;
}

.item-card:hover {
  background: rgba(255, 255, 255, 0.9);
  border-color: #475569;
  box-shadow: 0 4px 12px rgba(71, 85, 105, 0.15);
}

.item-card-error {
  border: 2px solid #dc2626 !important;
  background: rgba(254, 226, 226, 0.3) !important;
}

.item-label {
  font-weight: 500;
  color: #1e293b;
  font-size: 0.95rem;
}

.radio-group-custom >>> .v-input__control {
  justify-content: flex-start;
}

.radio-group-custom >>> .v-radio {
  margin-right: 24px;
}

.nexus-btn {
  background: linear-gradient(135deg, #475569 0%, #334155 100%) !important;
  color: white !important;
  border-radius: 12px !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px !important;
  text-transform: none !important;
  box-shadow: 0 4px 14px 0 rgba(71, 85, 105, 0.3) !important;
  transition: all 0.3s ease !important;
}

.nexus-btn:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 8px 25px 0 rgba(71, 85, 105, 0.4) !important;
}

.nexus-btn-secondary {
  background: rgba(255, 255, 255, 0.95) !important;
  color: #475569 !important;
  border-radius: 12px !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px !important;
  text-transform: none !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: 0 2px 8px 0 rgba(71, 85, 105, 0.15) !important;
  transition: all 0.3s ease !important;
}

.nexus-btn-secondary:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 6px 20px 0 rgba(71, 85, 105, 0.25) !important;
  border-color: #475569 !important;
}

.back-button {
  color: white !important;
  background: rgba(71, 85, 105, 0.9) !important;
  backdrop-filter: blur(10px) !important;
  border-radius: 50% !important;
  position: absolute;
  top: 24px;
  left: 24px;
  transition: all 0.3s ease !important;
  box-shadow: 0 4px 14px 0 rgba(71, 85, 105, 0.4) !important;
}

.back-button:hover {
  transform: scale(1.1) !important;
  box-shadow: 0 8px 25px 0 rgba(71, 85, 105, 0.5) !important;
}

.uploading-text {
  color: #475569;
  font-weight: 500;
  font-size: 0.9rem;
}

.nexus-dialog {
  border-radius: 20px !important;
  background: rgba(255, 255, 255, 0.98) !important;
  backdrop-filter: blur(10px) !important;
}

.nexus-dialog-title {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%) !important;
  color: #1e293b !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px !important;
  border-bottom: 1px solid #e2e8f0 !important;
  display: flex;
  align-items: center;
}

.photo-dialog-header {
  background: linear-gradient(135deg, #475569 0%, #334155 100%) !important;
}

.upload-option-btn {
  background: rgba(248, 250, 252, 0.9) !important;
  color: #1e293b !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 12px !important;
  font-weight: 500 !important;
  text-transform: none !important;
  transition: all 0.3s ease !important;
}

.upload-option-btn:hover {
  background: rgba(255, 255, 255, 1) !important;
  border-color: #475569 !important;
  box-shadow: 0 4px 12px rgba(71, 85, 105, 0.2) !important;
}

.v-overlay >>> .v-overlay__content {
  background: rgba(15, 23, 42, 0.8) !important;
  backdrop-filter: blur(4px) !important;
  border-radius: 16px !important;
  padding: 30px !important;
}

::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #475569 0%, #334155 100%);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(135deg, #334155 0%, #1e293b 100%);
}

@media (max-width: 768px) {
  .page-title {
    font-size: 1.75rem;
    letter-spacing: 1px;
  }

  .section-title {
    font-size: 1.1rem;
  }

  .section-title-text {
    font-size: 1.1rem;
  }

  .main-card {
    margin: 10px;
    border-radius: 16px;
  }

  .main-card >>> .v-card__text {
    padding: 20px !important;
  }
}
</style>